import React, { useRef, useState, useEffect } from 'react';
import { useAppContext } from '../contexts/AppContext';
import { useColorPicker } from '../hooks/useColorPicker';
import { markers } from '../utils/data';
import { getUserCollection } from '../utils/userCollection';

import BrandSelector from '../components/BrandSelector';
import ImageCanvas from '../components/ImageCanvas';
// eslint-disable-next-line no-unused-vars
import ColorMatchResult from '../components/ColorMatchResult';
import FinalPalette from '../components/FinalPalette';

import SharePaletteModal from '../components/SharePaletteModal';
import SavePaletteModal from '../components/SavePaletteModal';
import PageHelpModal from '../components/PageHelpModal';
import { encodePaletteForShare } from '../utils/paletteCode';
import { useI18n } from '../contexts/I18nContext';

import { Pipette, Palette, Camera, PencilIcon, Target, Share, X, Check, Heart } from 'lucide-react';

export default function ColorPickerPage() {
  const { brand, setBrand } = useAppContext();
  const { t } = useI18n();

  const fileInputRef = useRef(null);
  const finalPaletteRef = useRef(null);
  const colorsSidebarRef = useRef(null);
  const mobileColorsRef = useRef(null);
  const [showFinalPalette, setShowFinalPalette] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [isSaveOpen, setIsSaveOpen] = useState(false);
  const [shareCode, setShareCode] = useState('');
  const [sharePalette, setSharePalette] = useState([]);
  const imageCanvasRef = useRef(null);
  
  const {
    selectedImage,
    pickedColors,
    isPickingMode,
    cursorPosition,
    hoveredColor,
    startPicking,
    pickColorAt,
    removePickedColor,
    replaceRecommendedMarker,
    handleCursorMove,
    hasPickedColors
  } = useColorPicker();

  // Auto-scroll to latest color when new color is picked
  useEffect(() => {
    if (pickedColors.length > 3) {
      setTimeout(() => {
        if (colorsSidebarRef.current) {
          const container = colorsSidebarRef.current;
          const hasScrollableContent = container.scrollHeight > container.clientHeight;
          if (hasScrollableContent) {
            container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
          }
        }
        if (mobileColorsRef.current) {
          const container = mobileColorsRef.current;
          const hasScrollableContent = container.scrollWidth > container.clientWidth;
          if (hasScrollableContent) {
            container.scrollTo({ left: container.scrollWidth, behavior: 'smooth' });
          }
        }
      }, 200);
    }
  }, [pickedColors.length]);

  // Get available markers based on selected brand
  const getAvailableMarkers = () => {
    if (brand === "All") return markers;
    if (brand === "My Collection") {
      return getUserCollection();
    }
    return markers.filter(m => m.brand === brand);
  };

  // Handle image upload
  const handleImageUpload = (event) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      startPicking(file);
    }
  };

  // Handle color pick from canvas
  const handleColorPick = async (canvas, x, y) => {
    const availableMarkers = getAvailableMarkers();
    if (!availableMarkers.length) {
      alert(t('colorPicker.alerts.emptyCollection'));
      return;
    }
    try {
      await pickColorAt(canvas, x, y, availableMarkers);
    } catch (error) {
      console.error('Failed to pick color:', error);
    }
  };

  // Handle alternative marker selection
  // eslint-disable-next-line no-unused-vars
  const handleSelectAlternative = (colorId, newMarker) => {
    replaceRecommendedMarker(colorId, newMarker);
  };

  // Handle finalize palette with auto-scroll on mobile
  const handleFinalizePalette = () => {
    setShowFinalPalette(true);
    // Auto-scroll on mobile
    setTimeout(() => {
      if (finalPaletteRef.current && window.innerWidth < 768) {
        finalPaletteRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);

    // Auto-scroll sidebar on desktop
    setTimeout(() => {
    // Auto-scroll final palette on desktop
      if (finalPaletteRef.current) {
        finalPaletteRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // Handle new image upload - auto scroll to image canvas on mobile
  const handleImageUploadAndScroll = (event) => {
    handleImageUpload(event);
    // Auto-scroll to image canvas on mobile after upload
    setTimeout(() => {
      if (imageCanvasRef.current && window.innerWidth < 768) {
        imageCanvasRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest'
        });
      }
    }, 300);
  };  // Share modal helpers
  const openShareModalWithPalette = (palette) => {
    try {
      const code = encodePaletteForShare(palette || []);
      setShareCode(code);
      setSharePalette(palette || []);
      setIsShareOpen(true);
    } catch (e) {
      console.error('Failed to encode share code:', e);
    }
  };

  const getPickedPalette = () => {
    const uniquePalette = Array.from(new Set(pickedColors.map(pick => pick.recommendedMarker.hex)))
      .map(hex => pickedColors.find(pick => pick.recommendedMarker.hex === hex));
    return uniquePalette.map(pick => ({
      ...pick.recommendedMarker,
      hex: pick.recommendedMarker.hex,
      name: pick.recommendedMarker.name,
      brand: pick.recommendedMarker.brand,
      code: pick.recommendedMarker.code,
      code_old: pick.recommendedMarker.code_old,
      code_new: pick.recommendedMarker.code_new
    }));
  };

  const handleSharePalette = () => {
    const palette = getPickedPalette();
    openShareModalWithPalette(palette);
  };

  const handleSavePalette = () => {
    if (!hasPickedColors) return;
    setIsSaveOpen(true);
  };
  
  return (
    <div className="w-full mx-auto sm:px-6 lg:px-8 relative">
      {/* Mobile Floating Action Buttons */}
      {hasPickedColors && (
        <div className={`fixed right-4 z-[60] flex flex-col gap-3 md:hidden ${
          isPickingMode ? 'bottom-32' : 'bottom-6'
        }`}>
          <button
            onClick={handleSharePalette}
            className="w-16 h-16 bg-gradient-to-r from-purple-400 to-pink-500 text-white rounded-full shadow-lg flex items-center justify-center text-xl hover:shadow-xl transition-all transform hover:scale-105"
            title={t('randomPalette.share')}
          >
            <Share size={24} />
          </button>
          <button
            onClick={handleSavePalette}
            className="w-16 h-16 bg-gradient-to-r from-rose-pastel-400 to-pink-400 text-white rounded-full shadow-lg flex items-center justify-center text-xl hover:shadow-xl transition-all transform hover:scale-105"
            title={t('savedPalettes.saveModal.title')}
          >
            <Heart size={24} />
          </button>
          {!showFinalPalette && (
            <button
              onClick={handleFinalizePalette}
              className="w-16 h-16 bg-gradient-to-r from-green-500 to-cyan-500 text-white rounded-full shadow-lg flex items-center justify-center hover:shadow-xl transition-all transform hover:scale-105"
              title={t('colorPicker.finalize')}
            >
              <Check size={24} />
            </button>
          )}
        </div>
      )}

      {/* Welcome Section */}
      <div className="text-center mb-6">
        <h2 className="text-3xl md:text-4xl font-display font-bold heading-display mb-3 animate-pulse bg-gradient-to-r from-purple-400 to-violet-500">
          {t('colorPicker.title')}
          <Pipette className="inline-block w-8 h-8 ml-2 text-purple-500 pb-1" />
        </h2>
        <p className="text-base md:text-md text-elegant max-w-2xl mx-auto">
          {t('colorPicker.subtitle')}
        </p>
        <PageHelpModal
          title={t('colorPicker.help.title')}
          tips={[
            { text: t('colorPicker.help.tip1') },
            { text: t('colorPicker.help.tip2') },
            { text: t('colorPicker.help.tip3') },
            { text: t('colorPicker.help.tip4') },
          ]}
          trigger={
            <p className="text-sm justify-center text-slate-400 hover:text-slate-600 transition-colors cursor-pointer animate-pulse mt-2 mx-auto inline-flex items-center gap-2">
              {t('common.needHelp')}
            </p>
          }
        />
      </div>

      {/* Controls */}
      <div className="card-elegant p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <BrandSelector 
            value={brand} 
            onChange={setBrand} 
          />
          
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUploadAndScroll}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="btn btn-primary-green text-base md:text-lg px-4 py-3 w-full"
              disabled={isPickingMode}
            >
              <Camera className="inline-block w-5 h-5 mr-2 -ml-1" />
             {t('colorPicker.uploadBtn')}
            </button>
          </div>
        </div>
      </div>

      {/* MOBILE: Fixed bottom color bar */}
      {isPickingMode && !showFinalPalette &&  (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-lg border-t border-slate-200 shadow-2xl">
          <div className="p-3">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full flex items-center justify-center">
                  {/* <span className="text-white text-xs">🎨</span> */}
                  <Palette className="absolute w-4 h-4 text-slate-700" />
                </div>
                <span className="font-semibold text-sm text-slate-700">
                  {t('colorPicker.colorsLabel')} ({pickedColors.length})
                </span>
              </div>
            </div>
            
            <div 
              ref={mobileColorsRef}
              className="flex gap-2 overflow-x-auto pb-1 scrollbar-thin scrollbar-thumb-purple-300 scroll-smooth"
            >
              {pickedColors.length === 0 ? (
                <div className="flex-1 text-center text-slate-500 py-3 bg-slate-50 rounded-lg">
                  <span className="text-xs">{t('colorPicker.tapHint')}</span>
                </div>
              ) : (
                pickedColors.map((pickedColor) => (
                  <div 
                    key={pickedColor.id} 
                    className="flex-shrink-0 bg-white rounded-lg border border-slate-200 p-2 shadow-sm min-w-[80px] animate-slideIn"
                  >
                    <div className="flex flex-col items-center gap-1">
                      <div 
                        className="w-10 h-10 rounded-md border-2 border-white shadow-md"
                        style={{ backgroundColor: pickedColor.recommendedMarker.hex }}
                      />
                      <button
                        onClick={() => removePickedColor(pickedColor.id)}
                        className=" mt-1 w-6 h-6 rounded-full bg-red-50 text-red-500 hover:bg-red-100 text-xs flex items-center justify-center"
                        title={t('colorPicker.removeColor')}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="mb-6">
        {/* Image picking interface */}
        {isPickingMode && (
          <div className="lg:grid lg:gap-6 lg:grid-cols-12">
            {/* Canvas */}
            <div className="lg:col-span-10">
              <div ref={imageCanvasRef} className="card-elegant p-4 md:p-6">
                <div className="text-center mb-4">
                  <h3 className="text-lg md:text-xl font-display font-semibold text-slate-700 mb-2">
                    {t('colorPicker.selectFromImage')}
                  </h3>
                  <div className="w-16 h-1 bg-gradient-to-r from-rose-pastel-400 to-lavender-400 rounded-full mx-auto"></div>
                </div>
                
                <div className="mb-16 md:mb-0">
                  <ImageCanvas
                    selectedImage={selectedImage}
                    onColorPick={handleColorPick}
                    isPickingMode={isPickingMode}
                    onCursorMove={handleCursorMove}
                    hoveredColor={hoveredColor}
                    cursorPosition={cursorPosition}
                  />
                </div>
              </div>
            </div>

            {/* Sidebar cores (desktop) */}
            <div className="hidden lg:block lg:col-span-2">
              <div className="card-elegant p-3 flex flex-col" style={{ height: 'fit-content', maxHeight: '600px' }}>
                <div className="text-center mb-3 flex-shrink-0">
                  <h3 className="text-sm font-display font-semibold text-slate-700 mb-1">
                    {t('colorPicker.colorsLabel')}
                  </h3>
                  <p className="text-sm text-slate-600 mb-2">
                    {pickedColors.length}
                  </p>
                  <div className="w-6 h-0.5 bg-gradient-to-r from-rose-pastel-400 to-lavender-400 rounded-full mx-auto"></div>
                </div>
                
                <div 
                  ref={colorsSidebarRef}
                  className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin space-y-2 scroll-smooth" 
                  style={{ maxHeight: '480px' }}
                >
                  {pickedColors.length === 0 ? (
                    <div className="text-center py-6 text-slate-500">
                      <Palette className="w-8 h-8 mx-auto mb-2 text-purple-400" />
                      <p className="text-xs leading-relaxed">{t('colorPicker.clickColorsShort')}</p>
                    </div>
                  ) : (
                    pickedColors.map((pickedColor) => (
                      <div key={pickedColor.id} className="relative group animate-slideIn">
                        <div className="flex flex-col items-center gap-1">
                          <div className="relative">
                            <div 
                              className="w-16 h-16 rounded-xl shadow-lg border-2 border-white/50"
                              style={{ backgroundColor: pickedColor.recommendedMarker.hex }}
                              title={pickedColor.recommendedMarker.name}
                            />
                          </div>
                          <div className="text-center w-full px-0.5">
                            <p className="text-xs font-semibold text-slate-800 truncate leading-tight">
                              {pickedColor.recommendedMarker.name}
                            </p>
                            <p className="text-xs text-blue-600 truncate">
                              {pickedColor.recommendedMarker.brand}
                            </p>
                            <p className="text-xs font-mono text-slate-600 bg-slate-50 px-1 py-0.5 rounded mt-1">
                              {pickedColor.recommendedMarker.code}
                            </p>
                          </div>
                          <button
                            onClick={() => removePickedColor(pickedColor.id)}
                            className="absolute top-0 right-2 w-5 h-5 rounded-full bg-red-500 text-white hover:bg-red-600 text-xs transition-all shadow-sm opacity-80 hover:opacity-100"
                            title={t('colorPicker.removeColor')}
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {pickedColors.length > 0  && (
                  <div className="mt-4 flex-shrink-0">
                    <button
                      onClick={handleFinalizePalette}
                      className="btn btn-primary-green text-base px-4 py-2 w-full"
                    >
                      <Check className="inline-block w-7 h-7 mr-2 -ml-1" />
                      {t('colorPicker.finalize')}
                    </button> 
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Instructions */}
        {!isPickingMode && !hasPickedColors && (
          <div className="card-elegant p-8 md:p-10 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 border border-emerald-100">
            <div className="text-center mb-8">
              <h4 className="text-2xl md:text-3xl font-display font-bold text-slate-800 mb-3">
                {t('colorPicker.howTo.title')}
              </h4>
              <p className="text-slate-600 text-base md:text-lg max-w-2xl mx-auto">
                {t('colorPicker.howTo.subtitle')}
              </p>
              <div className="w-20 h-1 bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full mx-auto mt-4"></div>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6 md:gap-8">
              <div className="text-center space-y-4 bg-white/50 rounded-2xl p-6 backdrop-blur-sm border border-white/20">
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center mx-auto shadow-lg">
                  <Camera className="text-white w-6 h-6 md:w-8 md:h-8" />
                </div>
                <h5 className="font-bold text-slate-800 text-lg md:text-xl">{t('colorPicker.howTo.step1.title')}</h5>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {t('colorPicker.howTo.step1.desc')}
                </p>
              </div>
              
              <div className="text-center space-y-4 bg-white/50 rounded-2xl p-6 backdrop-blur-sm border border-white/20">
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center mx-auto shadow-lg">
                  <Target className="text-white w-6 h-6 md:w-8 md:h-8" />
                </div>
                <h5 className="font-bold text-slate-800 text-lg md:text-xl">{t('colorPicker.howTo.step2.title')}</h5>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {t('colorPicker.howTo.step2.desc')}
                </p>
              </div>
              
              <div className="text-center space-y-4 bg-white/50 rounded-2xl p-6 backdrop-blur-sm border border-white/20">
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center mx-auto shadow-lg">
                  <PencilIcon className="text-white w-6 h-6 md:w-8 md:h-8" />
                </div>
                <h5 className="font-bold text-slate-800 text-lg md:text-xl">{t('colorPicker.howTo.step3.title')}</h5>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {t('colorPicker.howTo.step3.desc')}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Final Palette */}
      {showFinalPalette && hasPickedColors && (
        <div className="mt-6" ref={finalPaletteRef} id="final-palette">
          <FinalPalette
            pickedColors={pickedColors}
            brand={brand}
            ohuhuMode="both"
          />
        </div>
      )}

      {/* Share button - Hidden on mobile since floating button exists */}
      {showFinalPalette && hasPickedColors && (
        <div className="text-center my-12 hidden md:block">
          <div className="flex items-center justify-center gap-3">
            <button 
              className="btn text-lg px-6 py-3 flex items-center gap-2 bg-gradient-to-r from-rose-pastel-300 to-pink-300 hover:from-rose-pastel-400 hover:to-pink-400 text-white transition-all"
              onClick={handleSavePalette}
            >
              <Heart className="inline-block w-5 h-5" />
              <span>Save</span>
            </button>
            <button 
              className="btn btn-primary text-lg px-6 py-3 flex items-center gap-2" 
              onClick={handleSharePalette}
            >
              <Share className="inline-block w-5 h-5" />
              <span>{t('colorPicker.sharePalette')}</span>
            </button>
          </div>
        </div>
      )}

      <SavePaletteModal
        isOpen={isSaveOpen}
        onClose={() => setIsSaveOpen(false)}
        palette={getPickedPalette()}
      />


      
      <SharePaletteModal
        isOpen={isShareOpen}
        onClose={() => setIsShareOpen(false)}
        code={shareCode}
        palette={sharePalette}
        ohuhuMode="both"
      />
    </div>
  );
}
